import type MarkdownIt from 'markdown-it';
import type Token from 'markdown-it/lib/token.mjs';

type StateBlock = Parameters<
  Parameters<MarkdownIt['block']['ruler']['before']>[2]
>[0];

/**
 * Canonical callout types. MkDocs "admonition" type names (and their
 * aliases, see {@link CALLOUT_TYPE_ALIASES}) are normalized to one of these.
 */
export const CALLOUT_TYPES = [
  'note',
  'abstract',
  'info',
  'tip',
  'success',
  'question',
  'warning',
  'failure',
  'danger',
  'bug',
  'example',
  'quote',
] as const;

export type CalloutType = (typeof CALLOUT_TYPES)[number];

/**
 * Every accepted type word (lowercase) and the canonical type it maps to.
 * Aliases are normalized on parse, so serializing writes the canonical word
 * — an intentional, visible normalization (covered by specs).
 */
export const CALLOUT_TYPE_ALIASES: Readonly<Record<string, CalloutType>> = {
  note: 'note',
  abstract: 'abstract',
  summary: 'abstract',
  tldr: 'abstract',
  info: 'info',
  todo: 'info',
  tip: 'tip',
  hint: 'tip',
  important: 'tip',
  success: 'success',
  check: 'success',
  done: 'success',
  question: 'question',
  help: 'question',
  faq: 'question',
  warning: 'warning',
  caution: 'warning',
  attention: 'warning',
  failure: 'failure',
  fail: 'failure',
  missing: 'failure',
  danger: 'danger',
  error: 'danger',
  bug: 'bug',
  example: 'example',
  quote: 'quote',
  cite: 'quote',
};

/**
 * Resolve a (case-insensitive) type word or alias to its canonical callout
 * type, or `null` when the word is not a known callout type.
 */
export function resolveCalloutType(word: string): CalloutType | null {
  const key = word.toLowerCase();
  return Object.hasOwn(CALLOUT_TYPE_ALIASES, key)
    ? (CALLOUT_TYPE_ALIASES[key] ?? null)
    : null;
}

/** Marker that opens an always-expanded callout. */
export const CALLOUT_MARKER = '!!!';
/** Marker that opens a collapsible (initially collapsed) callout. */
export const CALLOUT_COLLAPSED_MARKER = '???';

/** Body lines are indented by this many columns, as in MkDocs. */
export const CALLOUT_BODY_INDENT = 4;

export const CALLOUT_TYPE_ATTR = 'data-callout-type';
export const CALLOUT_TITLE_ATTR = 'data-callout-title';
export const CALLOUT_COLLAPSED_ATTR = 'data-callout-collapsed';

export type CalloutTokenMetadata = {
  type: CalloutType;
  /** `null` when the header has no quoted title; `''` is an explicit empty title. */
  title: string | null;
  collapsed: boolean;
};

export type CalloutHeader = CalloutTokenMetadata & {
  marker: typeof CALLOUT_MARKER | typeof CALLOUT_COLLAPSED_MARKER;
};

const HEADER_RE =
  /^(!!!|\?\?\?)[ \t]+([A-Za-z][A-Za-z0-9_-]*)(?:[ \t]+"(.*)")?[ \t]*$/u;

/**
 * Parse a callout header line such as `!!! note "Title"` or `??? warning`.
 * Returns `null` for anything else — including unknown type words, which
 * keeps the source as ordinary Markdown instead of guessing a type.
 */
export function parseCalloutHeader(line: string): CalloutHeader | null {
  const match = HEADER_RE.exec(line);
  if (!match) {
    return null;
  }
  const [, marker, word, title] = match;
  if (!word) {
    return null;
  }
  const type = resolveCalloutType(word);
  if (!type) {
    return null;
  }
  const collapsed = marker === CALLOUT_COLLAPSED_MARKER;
  return {
    marker: collapsed ? CALLOUT_COLLAPSED_MARKER : CALLOUT_MARKER,
    type,
    title: title ?? null,
    collapsed,
  };
}

/** Collapse line breaks so a title always fits on the single header line. */
function sanitizeCalloutTitle(title: string): string {
  return title.replace(/\s*[\r\n]+\s*/gu, ' ');
}

/** Render the canonical header line for a callout. */
export function serializeCalloutHeader(meta: CalloutTokenMetadata): string {
  const marker = meta.collapsed ? CALLOUT_COLLAPSED_MARKER : CALLOUT_MARKER;
  const title =
    meta.title === null ? '' : ` "${sanitizeCalloutTitle(meta.title)}"`;
  return `${marker} ${meta.type}${title}`;
}

const LINE_START_HEADER_RE = /^(?:!!!|\?\?\?)[ \t]+([A-Za-z][A-Za-z0-9_-]*)/u;

/**
 * Preserve literal paragraph text that would otherwise be reparsed as a
 * callout header. Deliberately looser than {@link parseCalloutHeader}: a
 * text node may hold only the start of a header line (the rest of it sits
 * in a differently-marked node), so any marker followed by a known type
 * word is escaped. Escaping the first marker character is enough to break
 * the match; Markdown unescapes it back to the literal text.
 */
export function escapeCalloutLineStart(text: string): string {
  const word = LINE_START_HEADER_RE.exec(text)?.[1];
  return word && resolveCalloutType(word) ? `\\${text}` : text;
}

/**
 * Read the engine-neutral callout metadata stamped by {@link calloutTokenizer}
 * on a `callout_open` token.
 */
export function readCalloutTokenMetadata(token: Token): CalloutTokenMetadata {
  const type = resolveCalloutType(token.attrGet(CALLOUT_TYPE_ATTR) ?? '');
  return {
    type: type ?? 'note',
    title: token.attrGet(CALLOUT_TITLE_ATTR),
    collapsed: token.attrGet(CALLOUT_COLLAPSED_ATTR) === 'true',
  };
}

/**
 * markdown-it block plugin for MkDocs-style callouts:
 *
 * ```
 * !!! note "Optional title"
 *     Body markdown…
 *
 * ??? warning "Collapsed"
 *     Body…
 * ```
 *
 * The body is every following line indented by four columns (blank lines in
 * between are part of the body); it is tokenized as ordinary nested blocks
 * between a `callout_open` and `callout_close` token. The open token carries
 * the canonical type, optional title and collapsed flag as attrs, readable
 * with {@link readCalloutTokenMetadata}.
 *
 * Anything that is not an unambiguous header (unknown type word, extra CSS
 * classes, missing space, …) declines so the source keeps its ordinary
 * CommonMark meaning. A header may not interrupt a paragraph, matching
 * MkDocs, which requires a blank line before it.
 */
export function calloutTokenizer(md: MarkdownIt): void {
  md.block.ruler.before(
    'fence',
    'callout',
    (state, startLine, endLine, silent) => {
      // Four or more columns of indent is an indented code block.
      if (indentOf(state, startLine) - state.blkIndent >= 4) {
        return false;
      }
      const header = parseCalloutHeader(lineText(state, startLine));
      if (!header) {
        return false;
      }

      const bodyIndent = state.blkIndent + CALLOUT_BODY_INDENT;
      let lastBodyLine = startLine;
      for (let line = startLine + 1; line < endLine; line++) {
        if (state.isEmpty(line)) {
          continue;
        }
        if (indentOf(state, line) < bodyIndent) {
          break;
        }
        lastBodyLine = line;
      }

      if (silent) {
        return true;
      }

      const bodyEnd = lastBodyLine + 1;
      const open = state.push('callout_open', 'div', 1);
      open.block = true;
      open.markup = header.marker;
      open.map = [startLine, bodyEnd];
      open.attrSet(CALLOUT_TYPE_ATTR, header.type);
      if (header.title !== null) {
        open.attrSet(CALLOUT_TITLE_ATTR, header.title);
      }
      open.attrSet(CALLOUT_COLLAPSED_ATTR, String(header.collapsed));

      const oldParentType = state.parentType;
      const oldBlkIndent = state.blkIndent;
      state.parentType = 'blockquote';
      state.blkIndent = bodyIndent;
      state.md.block.tokenize(state, startLine + 1, bodyEnd);
      state.blkIndent = oldBlkIndent;
      state.parentType = oldParentType;

      const close = state.push('callout_close', 'div', -1);
      close.block = true;
      close.markup = header.marker;

      state.line = bodyEnd;
      return true;
    },
  );
}

function indentOf(state: StateBlock, line: number): number {
  return state.sCount[line] ?? 0;
}

function lineText(state: StateBlock, line: number): string {
  const start = (state.bMarks[line] ?? 0) + (state.tShift[line] ?? 0);
  return state.src.slice(start, state.eMarks[line] ?? 0);
}
